import React from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath, Redirect, useHistory, useLocation} from 'react-router-dom';

import {exportProductCardAuditLog, fetchProductCardAuditLog} from 'api/productCardAudit';
import {auditColumns} from 'components/common/AuditLog/columns';
import {AuditExportControl} from 'components/common/AuditLog/ExportControl';
import {AuditNavigation} from 'components/common/AuditLog/Navigation';
import {PageContent, SearchFields, Toolbar} from 'components/common/AuditLog/styled';
import {useAuditLog} from 'components/common/AuditLog/useAuditLog';
import {Page} from 'components/common/Page';
import {RecallAuditLogPage} from 'components/pages/RecallAudit';
import {formatLocalDateTime, translateUserRole} from 'components/pages/RecallAudit/utils';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {PRODUCT_CARD_EVENTS} from 'types/product-card-audit';

import {mapProductCardAuditFilters} from './filters';

const activityLogPages = [
    {
        permission: PERMISSION_ALIASES.recallAuditLogView,
        render: () => <RecallAuditLogPage />,
    },
    {
        permission: PERMISSION_ALIASES.productCardAuditLogView,
        render: () => <Redirect to={ROUTE.PRODUCT_CARD_AUDIT} />,
    },
    {
        permission: PERMISSION_ALIASES.remarkingAuditLogView,
        render: () => <Redirect to={ROUTE.REMARKING_AUDIT} />,
    },
    {
        permission: PERMISSION_ALIASES.blockingAuditLogView,
        render: () => <Redirect to={ROUTE.BLOCKING_AUDIT} />,
    },
    {
        permission: PERMISSION_ALIASES.withdrawalAuditLogView,
        render: () => <Redirect to={ROUTE.WITHDRAWAL_AUDIT} />,
    },
    {
        permission: PERMISSION_ALIASES.returnAuditLogView,
        render: () => <Redirect to={ROUTE.RETURN_AUDIT} />,
    },
];

export const ActivityLogPage = () => {
    const permissions = useStore(permissions$);
    const page = activityLogPages.find(({permission}) => permissions.includes(permission));
    return page ? page.render() : null;
};

export const ProductCardAuditLogPage = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const permissions = useStore(permissions$);
    const {
        filters,
        page,
        size,
        data,
        pending,
        setPage,
        setSize,
        changeFilters,
        resetFilters,
    } = useAuditLog(fetchProductCardAuditLog, mapProductCardAuditFilters, {
        productId: new URLSearchParams(location.search).get('productId') || '',
    });
    const fields = auditColumns(
        t,
        PRODUCT_CARD_EVENTS.map((value) => ({
            value,
            label: t(`pages.productCardAudit.events.${value}`),
        })),
        true,
    );
    const rows = (data?.data || []).map((entry) => ({
        ...entry,
        eventId: (
            <Link to={generatePath(ROUTE.PRODUCT_CARD_AUDIT_DETAIL, {id: entry.eventId})}>
                {entry.eventId}
            </Link>
        ),
        eventType: t(`pages.productCardAudit.events.${entry.eventType}`, {
            defaultValue: entry.eventType,
        }),
        eventTimestamp: formatLocalDateTime(entry.eventTimestamp),
        userRole: translateUserRole(t, entry.userRole),
        organizationName: entry.organizationName ?? entry.organizationId ?? '—',
        organizationTin: entry.organizationTin || '—',
        userFullName: entry.userFullName || entry.userId || '—',
        entry,
    }));
    return (
        <Page title={t('menu.activityLog')}>
            <PageContent>
                <AuditNavigation />
                <Table
                    headerControls={
                        <Toolbar>
                            <SearchFields>
                                <Input
                                    placeholder={t('pages.productCardAudit.search')}
                                    aria-label={t('pages.productCardAudit.search')}
                                    value={String(filters.search || filters.productId || '')}
                                    onChange={(event) =>
                                        changeFilters([
                                            {id: 'search', value: event.target.value},
                                            {id: 'productId', value: undefined},
                                        ])
                                    }
                                />
                            </SearchFields>
                            {permissions.includes(PERMISSION_ALIASES.productCardAuditLogExport) && (
                                <AuditExportControl
                                    fileName="product-card-audit"
                                    initialPeriod={
                                        filters.eventTimestamp as [Date | null, Date | null]
                                    }
                                    onExport={(format, period) => {
                                        const exportParams = mapProductCardAuditFilters({
                                            ...filters,
                                            eventTimestamp: period,
                                        });
                                        return exportProductCardAuditLog({
                                            ...exportParams,
                                            from: exportParams.from!,
                                            to: exportParams.to!,
                                            format,
                                            page,
                                            size,
                                        });
                                    }}
                                />
                            )}
                        </Toolbar>
                    }
                    columns={mapTableColumns(
                        fields,
                        filters,
                        changeFilters,
                        resetFilters,
                        {
                            eventTimestamp:
                                filters.sortDesc === undefined
                                    ? undefined
                                    : filters.sortDesc === 'true',
                        },
                        (values) =>
                            changeFilters(
                                values.map(({value}) => ({
                                    id: 'sortDesc',
                                    value: value === undefined ? undefined : String(value),
                                })),
                            ),
                    )}
                    data={rows}
                    pageIndex={page}
                    pageCount={data?.totalPages}
                    totalRowsCount={data?.totalElements}
                    onPageChange={setPage}
                    onPageSizeChange={(value) => {
                        setSize(value);
                        setPage(0);
                    }}
                    isLoading={pending}
                    onRowClick={(row) =>
                        history.push(
                            generatePath(ROUTE.PRODUCT_CARD_AUDIT_DETAIL, {
                                id: row.original.entry.eventId,
                            }),
                        )
                    }
                />
            </PageContent>
        </Page>
    );
};
